// src/admin/pages/WebhookLogs.js
import React, { useState, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Card, Badge, Spinner, Modal, Row, Col, Alert } from 'react-bootstrap';
import { Search, ShieldAlert, Terminal, Eye, X, HelpCircle } from 'lucide-react';

import { getWebhookLogs, getWebhookLogDetails } from '../../services/adminApi'; 

import './WebhookLogs.css';

const WebhookLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
    
    // Details Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchLogs = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                search,
                status: statusFilter,
                per_page: 12
            };
            const response = await getWebhookLogs(params);
            setLogs(response.data.data || []);
            setPagination({
                current_page: response.data.current_page || 1,
                last_page: response.data.last_page || 1,
                total: response.data.total || 0
            });
        } catch (err) {
            console.error('Could not load webhook logs.', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchLogs(1);
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [search, statusFilter]);

    const handleOpenDetails = async (id) => {
        setLoadingDetails(true);
        setShowModal(true);
        try {
            // ✅ adminApi फ़ंक्शन का उपयोग किया गया
            const response = await getWebhookLogDetails(id);
            setSelectedLog(response.data);
        } catch (err) {
            console.error('Could not load webhook details.', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'success':
                return <Badge bg="success" className="px-2 py-1">Success</Badge>;
            case 'ignored':
                return <Badge bg="secondary" className="px-2 py-1">Ignored</Badge>;
            case 'already_processed':
                return <Badge bg="info" className="px-2 py-1 text-white">Already Actioned</Badge>;
            case 'signature_failed':
                return <Badge bg="danger" className="px-2 py-1">Signature Fail</Badge>;
            case 'failed':
                return <Badge bg="warning" className="px-2 py-1 text-dark">Failed</Badge>;
            case 'error':
                return <Badge bg="danger" className="px-2 py-1">Error Exception</Badge>;
            default:
                return <Badge bg="light" className="px-2 py-1 text-dark">{status}</Badge>;
        }
    };

    const formatJSON = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return jsonString;
        }
    };

    return (
        <div className="container-fluid py-4 webhook-logs-container">
            <div className="mb-4">
                <h2 className="fw-bold text-white m-0 d-flex align-items-center gap-2" style={{ fontSize: '1.75rem' }}>
                    <Terminal className="text-danger" size={28} /> Webhook Delivery Auditing
                </h2>
                <p className="webhook-logs-subtitle">Review real-time Razorpay server logs and automated delivery attempts.</p>
            </div>

            <Card className="border-0 rounded-4 overflow-hidden mb-4 webhook-logs-card">
                <Card.Body className="p-4 bg-transparent webhook-logs-border-bottom">
                    <Row className="g-3">
                        <Col lg={8} md={6}>
                            <InputGroup className="border-0 rounded-3 webhook-logs-input-group">
                                <InputGroup.Text className="bg-transparent border-0 text-secondary">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by Payment ID, Order ID, Event Type..."
                                    className="bg-transparent border-0 shadow-none ps-0 webhook-logs-input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col lg={4} md={6}>
                            <Form.Select className="border-0 rounded-3 text-white py-2 webhook-logs-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="All" style={{ backgroundColor: '#151521' }}>All statuses</option>
                                <option value="success" style={{ backgroundColor: '#151521' }}>Success</option>
                                <option value="ignored" style={{ backgroundColor: '#151521' }}>Ignored</option>
                                <option value="already_processed" style={{ backgroundColor: '#151521' }}>Already Actioned</option>
                                <option value="signature_failed" style={{ backgroundColor: '#151521' }}>Signature Fail</option>
                                <option value="failed" style={{ backgroundColor: '#151521' }}>Failed</option>
                                <option value="error" style={{ backgroundColor: '#151521' }}>Error Exception</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>

                <div className="table-responsive">
                    <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless webhook-logs-table">
                        <thead className="webhook-logs-thead">
                            <tr className="webhook-logs-thead-tr">
                                <th className="px-4 py-3 text-uppercase">#</th>
                                <th className="py-3 text-uppercase">Event Name</th>
                                <th className="py-3 text-uppercase">Order ID</th>
                                <th className="py-3 text-uppercase">Payment ID</th>
                                <th className="py-3 text-uppercase">Delivery Status</th>
                                <th className="py-3 text-uppercase">Sender IP</th>
                                <th className="py-3 text-uppercase">Date & Time</th>
                                <th className="px-4 py-3 text-center text-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.9rem' }}>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="text-center p-5 webhook-logs-text-secondary" style={{ backgroundColor: '#151521' }}>
                                        <Spinner animation="border" className="me-2" size="sm" /> Fetching delivery logs...
                                    </td>
                                </tr>
                            ) : logs.length > 0 ? (
                                logs.map((log, idx) => (
                                    <tr key={log.id} className="webhook-logs-tbody-tr">
                                        <td className="px-4 webhook-logs-text-secondary">
                                            {(pagination.current_page - 1) * 12 + idx + 1}
                                        </td>
                                        <td className="fw-semibold text-white font-monospace">{log.event || 'N/A'}</td>
                                        <td className="font-monospace webhook-logs-text-secondary small">{log.razorpay_order_id || '-'}</td>
                                        <td className="font-monospace webhook-logs-text-secondary small">{log.razorpay_payment_id || '-'}</td>
                                        <td>{getStatusBadge(log.status)}</td>
                                        <td className="webhook-logs-text-secondary font-monospace small">{log.ip_address}</td>
                                        <td className="webhook-logs-text-secondary small">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                                        <td className="px-4 text-center">
                                            <Button variant="dark" size="sm" className="webhook-logs-btn-icon" onClick={() => handleOpenDetails(log.id)}>
                                                <Eye size={16} className="text-white" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center p-5 webhook-logs-text-secondary" style={{ backgroundColor: '#151521' }}>
                                        <ShieldAlert size={36} className="mb-2" />
                                        <p className="m-0">No webhook delivery logs match criteria.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* Pagination controls */}
            {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 rounded-4 shadow-sm" style={{ backgroundColor: '#151521', border: '1px solid #222531' }}>
                    <span className="webhook-logs-text-secondary small">Showing Page {pagination.current_page} of {pagination.last_page} ({pagination.total} records)</span>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" className="btn-sm webhook-logs-btn-secondary" disabled={pagination.current_page === 1} onClick={() => fetchLogs(pagination.current_page - 1)}>
                            Previous
                        </Button>
                        <Button variant="outline-secondary" className="btn-sm webhook-logs-btn-secondary" disabled={pagination.current_page === pagination.last_page} onClick={() => fetchLogs(pagination.current_page + 1)}>
                            Next
                        </Button>
                    </div>
                </div>
            )}

            {/* Log Inspector Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header className="webhook-logs-modal-header p-4">
                    <Modal.Title className="fw-bold text-white d-flex align-items-center gap-2" style={{ fontSize: '1.25rem' }}>
                        <Terminal size={22} className="text-danger" /> Webhook Payload Inspector
                    </Modal.Title>
                    <Button variant="dark" className="p-1 rounded-circle border-secondary bg-transparent ms-auto" onClick={() => setShowModal(false)}>
                        <X size={18} className="text-white" />
                    </Button>
                </Modal.Header>
                <Modal.Body className="webhook-logs-modal-body p-4">
                    {loadingDetails ? (
                        <div className="text-center p-5 webhook-logs-text-secondary"><Spinner animation="border" /> <p className="mt-2">Decoding JSON Payload...</p></div>
                    ) : selectedLog ? (
                        <div>
                            <Row className="mb-4 g-3">
                                <Col md={4}>
                                    <p className="mb-1 webhook-logs-text-secondary small">EVENT TRIGGER</p>
                                    <strong className="font-monospace text-white">{selectedLog.event || 'N/A'}</strong>
                                </Col>
                                <Col md={4}>
                                    <p className="mb-1 webhook-logs-text-secondary small">DELIVERY STATUS</p>
                                    {getStatusBadge(selectedLog.status)}
                                </Col>
                                <Col md={4}>
                                    <p className="mb-1 webhook-logs-text-secondary small">TIMESTAMP</p>
                                    <strong className="text-white">{new Date(selectedLog.created_at).toLocaleString('en-IN')}</strong>
                                </Col>
                            </Row>

                            {selectedLog.error_message && (
                                <Alert className="border-0 py-3 mb-4 rounded-3 d-flex gap-2 webhook-logs-alert-danger">
                                    <HelpCircle className="text-danger flex-shrink-0" size={18} />
                                    <div>
                                        <strong className="text-white d-block mb-1">Audit Failure Detail</strong>
                                        <span className="font-monospace small text-white">{selectedLog.error_message}</span>
                                    </div>
                                </Alert>
                            )}

                            <div>
                                <p className="mb-2 webhook-logs-text-secondary small">RAW WEBHOOK BODY (PAYLOAD JSON)</p>
                                <pre className="webhook-logs-pre-box">
                                    {formatJSON(selectedLog.payload)}
                                </pre>
                            </div>
                        </div>
                    ) : (
                        <Alert variant="warning">Payload data could not be fetched.</Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="webhook-logs-modal-footer p-3">
                    <Button variant="outline-secondary" className="webhook-logs-btn-secondary" onClick={() => setShowModal(false)}>
                        Close Inspector
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WebhookLogs;